import React, { type ComponentProps, useSyncExternalStore } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';

import { Footer as HTMLFooter } from '@expo/html-elements';

import { Container, HStack, theme, useNavbarContainerHeight, useResponsive } from '@sg/garnish';

import { useOrganizationLinksFooterContentfulData } from '../../hooks/useOrganizationLinksContentfulData';
import { OrganizationLinksFooterCopyright } from './components/OrganizationLinksFooterCopyright';
import { OrganizationLinksFooterSection } from './components/OrganizationLinksFooterSection';
import { useConditionalLinksRules } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationLinksFooter = (props: Props) => {
  const { style, isLandmark = true } = props;

  const contentfulFooter = useOrganizationLinksFooterContentfulData();
  const conditionalLinksRules = useConditionalLinksRules();

  const isWideViewport = useSyncExternalStore(
    subscribeToWindowChanges,
    checkIfWideViewport,
    checkIfWideViewport,
  );
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const { currentBreakpoint, minWidth, match } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyNavbarPadding = currentBreakpoint.isXS;
  const itemsPerRow = isWideViewport ? undefined : match([1, 2, 4]);
  const palette = 'dark';

  // ─── Styles ────────────────────────────────────────────────────────────────

  const linkGroupsGap = match([theme.spacing['6'], theme.spacing['6'], theme.spacing['10']]);
  const containerStyles = [styles.container, minWidth.isMD ? styles.containerMD : undefined];
  const linkGroupsStackStyles = minWidth.isMD ? styles.linkGroupsStackMD : undefined;

  const footerStyles = [
    styles.footer,
    styles.footerNavbarV2,
    shouldApplyNavbarPadding ? navbarPaddingBottomStyle : undefined,
    style,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  const FooterElement = isLandmark ? HTMLFooter : View;

  return (
    <FooterElement style={footerStyles}>
      <Container style={containerStyles}>
        <View style={linkGroupsStackStyles}>
          <HStack itemsPerRow={itemsPerRow} gap={linkGroupsGap}>
            {contentfulFooter.sections.map((section) => (
              <OrganizationLinksFooterSection
                key={section.name}
                section={section}
                conditionalLinksRules={conditionalLinksRules}
                palette={palette}
              />
            ))}
          </HStack>
        </View>

        <OrganizationLinksFooterCopyright palette={palette} />
      </Container>
    </FooterElement>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Subscribes to window size changes, so the footer only re-renders when the
 * wide-viewport check flips instead of on every resize.
 */
function subscribeToWindowChanges(onWindowChange: () => void) {
  const subscription = Dimensions.addEventListener('change', onWindowChange);

  return () => {
    subscription.remove();
  };
}

function checkIfWideViewport() {
  return Dimensions.get('window').width >= WIDE_VIEWPORT_MIN_WIDTH;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const WIDE_VIEWPORT_MIN_WIDTH = 1280;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    marginTop: 'auto',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.SKY,
    paddingVertical: theme.spacing['10'],
  },
  footerNavbarV2: {
    backgroundColor: theme.colors.BROWN,
  },
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
  },
  containerMD: {
    gap: theme.spacing['8'],
  },

  linkGroupsStackMD: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  style?: ComponentProps<typeof HTMLFooter>['style'];

  /**
   * NOTE: Landmarks must not nest, so callers rendering the footer inside
   *       another landmark opt out of the `contentinfo` element.
   */
  isLandmark?: boolean;
};
